import type { TabBar } from '../typings/base';
import { DATA_CENTER_DESCRIPTION_URL } from '@constants/address';
import { checkIsCollection, EffectType, MaterialCommonAttr } from '@heycan/lynx-common';

// 转场动画效果
export enum EnterMode {
  none = 'none', // 没有转场动画
  bottomToTop = 'modal', // 从下往上转场 （iOS 系统present默认动画）
  leftToRight = 'from_left_to_right', // 从左往右转场（系统push默认动画）
  leftToRightFull = 'from_left_to_right_full', // 从左往右转场，移动幅度为整个屏幕
  rightToLeft = 'from_right_to_left', // 从右往左转场
  rightToLeftFull = 'from_right_to_left_full' // 从右往左转场
}

export function appendQuery(schema: string, params: Params) {
  const [main, hash] = schema.split('#');
  const [base, _qs = ''] = main.split('?');
  const query = parseQueryString(_qs);

  const afterformatQs = stringifyQuery({ ...query, ...params });
  return `${base}${afterformatQs ? `?${afterformatQs}` : ''}${hash || hash === '' ? `#${hash}` : ''}`;
}

type Params = Record<string, string | number | boolean>;

// schema参数拼接
export function stringifyQuery(params: Params) {
  const pairs = Object.keys(params).map(k => {
    if (typeof params[k] === 'undefined') {
      return '';
    }
    return `${k}=${encodeURIComponent(params[k])}`;
  });

  return pairs.filter(Boolean).join('&');
}

export function parseQueryString(qs: string) {
  return qs.split('&').reduce((r, kv) => {
    const [k, v = ''] = kv.split('=');
    return k ? { ...r, [k]: decodeURIComponent(v) } : r;
  }, {});
}

export function modifySchemaQuery(schema: string, newQuery: Record<string, string>) {
  if (schema) {
    const [url, queryStr = ''] = schema.split('?');
    const originQuery = parseQueryString(queryStr);
    const hydrateQuery = stringifyQuery({ ...originQuery, ...newQuery });
    return `${url}${hydrateQuery ? `?${hydrateQuery}` : ''}`;
  }
  return schema;
}

export function getQueryString(name: string, decode = true): string | null {
  const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`, 'i');
  const r = window.location.search.substr(1).match(reg);
  if (r !== null) {
    return decode ? decodeURIComponent(r[2]) : r[2];
  }
  return null;
}

export function httpsify(url: string) {
  return (url || '').replace(/^http:/, 'https:');
}

// 通用 webview schema
export function genWebviewSchema(params: {
  web_url: string;
  enter_from?: string;
  title?: string;
  title_color?: string;
  hide_nav_bar?: 0 | 1;
  nav_bar_color?: string;
  loading_bgcolor?: string;
  status_bar_color?: string;
  status_font_dark?: 0 | 1;
  theme?: 'dark' | 'light';
}) {
  const preset: Partial<typeof params> = {};

  if (params.theme === 'light') {
    Object.assign(preset, {
      loading_bgcolor: 'ffffff',
      nav_bar_color: 'ffffff',
      status_bar_color: 'ffffff',
      status_font_dark: 1,
      title_color: '000000ff',
    });
  }

  return `capcut://main/web?${stringifyQuery({ ...preset, ...params })}`;
}

// 跳转个人主页 schema
export function genHomePageSchema(params: {
  user_id: string;
  enter_from?: string;
  tab?: string;
  trending?: string;
  trending_category?: string;
  rank?: number;
  root_category?: string;
  tab_name?: string;
  publicid?: string;
  profile_page_url?: string;
}) {
  return `capcut://user/homepage?${stringifyQuery(params)}`;
}

// 跳转剪同款（模版）页 schema
export function genTemplatePageSchema(params: {
  template_id: string;
  trending?: string;
  trending_category?: string;
  enter_from?: string;
  tab_name: string;
  root_category: string;
  rank?: number;
}) {
  return `capcut://template/detail?${stringifyQuery(params)}`;
}

// 教程详情页 通用参数https://bytedance.feishu.cn/wiki/wikcnhVEoLXMaGMNpu29ARQ4Ief
export function genTutorialDetailSchema(params: {
  /** 教程id */
  tutorial_id: number;
  /** 埋点需要，来源 */
  enter_from?: string;
  /** 埋点需要，搜索关键词来源 */
  keyword_source?: string;
  /** 埋点需要， 初始搜索词【用户输入的词】 */
  raw_query?: string;
  /** 埋点需要，实际搜索关键词 */
  search_keyword?: string;
  /** 埋点需要，搜索id */
  search_id?: string;
  /** 埋点需要, 教程名称 */
  tutorial_name?: string;
  /** 埋点需要，结果列表中搜索结果的排序位置，功能与教程两部分分开排序 */
  rank?: number;
  /** 埋点需要，搜索所处位置 */
  search_position?: string;
}) {
  return genLynxviewSchema({
    channel: 'image_lynx_search_tutorial_detail',
    bundle: 'template.js',
    loading_bgcolor: 'ffffff',
    hide_nav_bar: 1,
    status_bar_color: 'ffffff',
    status_font_dark: 1,
    hide_loading: 0,
    ...params,
  });
}

// 通用 Lynxview schema
export function genLynxviewSchema(params: {
  channel?: string;
  bundle?: string;
  silent_load?: 0 | 1;
  async_layout?: 0 | 1;
  hide_nav_bar?: 0 | 1;
  title?: string;
  theme?: 'dark' | 'light';
  title_color?: string;
  hide_loading?: 0 | 1;
  loading_bgcolor?: string;
  nav_bar_color?: string;
  immersive_mode?: 0 | 1;
  status_font_dark?: 1 | 0;
  status_bar_color?: string;
  [key: string]: any;
}) {
  return `capcut://main/lynx?${stringifyQuery(params)}`;
}

// 创作者权益页 schema
export function genCreatorRightsSchema() {
  return genLynxviewSchema({
    channel: 'image_lynx_creator_rights',
    bundle: 'template.js',
    loading_bgcolor: 'ffffff',
    status_bar_color: 'ffffff',
    status_font_dark: 1,
    hide_nav_bar: 1,
    theme: 'light',
    hide_loading: 0,
  });
}

// 跳任务中心
export function genBusinessTaskCenterSchema(options = {}) {
  return genLynxviewSchema({
    channel: 'image_lynx_creator_business_task',
    bundle: 'template.js',
    hide_loading: 1,
    loading_bgcolor: 'f2f5f7',
    status_bar_color: 'f2f5f7',
    status_font_dark: 1,
    hide_nav_bar: 1,
    theme: 'light',
    ...options,
  });
}

/** 会员订阅页面 */
/* export function genVipPageSchema(params?: {
  page_from?: string;
  hide_pro_tab?: string;
  template_id?: string;
  pay_source?: PaySource;
  position?: PayPosition;
  enter_source?: EnterSource;
}) {

  return genLynxviewSchema({
    ...(params || {}),
    bundle: 'template.js',
    channel: 'image_lynx_subscription',
    dynamic: 3,
    hide_nav_bar: 1,
    immersive_mode: 1,
    status_font_dark: 0,
    loading_white: 1, // lynx页面加载使用白色loading
    // is_debug: 1,
  });
} */

// 素材商店二级分类聚合页面
export function genMaterialDetailPageSchema(info: MaterialCommonAttr, extra = {}) {
  const isCollection = checkIsCollection(info.effect_type) || info.effect_type === EffectType.Emoticon;
  return genLynxviewSchema({
    bundle: isCollection ? 'pages/collection/template.js' : 'pages/single/template.js',
    channel: isCollection ?
      'image_lynx_heycan_new_material_detail_collection' :
      'image_lynx_heycan_new_material_detail_single',
    effect_id: info.effect_id || info.id,
    effect_type: info.effect_type,
    effect_source: info.source,
    theme: 'dark',
    hide_loading: 1,
    hide_status_bar: 0,
    hide_nav_bar: 1,
    immersive_mode: 1,
    dynamic: 3,
    ...extra,
  });
}

export function genDataCenterDescriptionUrl(params: { lang: string; title?: string }) {
  return genWebviewSchema({
    web_url: DATA_CENTER_DESCRIPTION_URL,
    hide_nav_bar: 0,
    theme: 'light',
    ...params,
  });
}

// 定位到 某个 底部 TabBar
export function genCheckoutTabBar(
  index: TabBar,
  options?: {
    enter_mode?: EnterMode;
  }
) {
  const params: Record<string, any> = {
    index,
    enter_mode: 'none',
    ...options,
  };
  return `capcut://main/tabbar?${stringifyQuery(params)}`;
}

/* 唤起模板创作 */
export function genEditCreateSchema(params: { [key: string]: any }) {
  return `capcut://editor/create?${stringifyQuery(params)}`;
}
